import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { ICmdTableUserCell, TTemplateRecords } from './interfaces';
import {
  TEMPLATE_TABLE_KEY,
  TEMPLATE_ATTACHMENT_KEY,
  TEMPLATE_FUEL_CARD_PROVIDER_ID_KEY,
  TEMPLATE_MIME_TYPE_GROUP_KEY,
  TEMPLATE_ENCODING_KEY,
} from './constants';
import ThSearch from '../CmdTable/TableHeadCell/ThSearch';
import { ICmdTableColumn } from '../CmdTable/types';
import ThOnlySort from '../CmdTable/TableHeadCell/ThOnlySort';
import ThSimpleSelect from '../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../CmdTable/TableHeadCell/ThMultiSelect';
import TextCell from '../CmdTable/TableCells/TextCell';
import DownloadSelect from '../DownloadSelect';
import { columnsAtom } from '../CmdTable/CmdTableState';

interface IUseTemplate {
  columns: ColumnDef<TTemplateRecords>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useTemplate(): IUseTemplate {
  const { formatMessage: f } = useIntl();
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);

  const columns = React.useMemo<ColumnDef<TTemplateRecords>[]>(
    () => [
      {
        accessorKey: TEMPLATE_ATTACHMENT_KEY,
        header: (table) => (
          <ThOnlySort
            tableName={TEMPLATE_TABLE_KEY}
            id={TEMPLATE_ATTACHMENT_KEY}
            title={f({
              id: `table_list.columns.importTemplates.${TEMPLATE_ATTACHMENT_KEY}`,
            })}
            // tooltip={f({ id: `table_list.columns.importTemplates.${TAG_COLOR_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.attachments ? (
            <DownloadSelect data={info.row.original?.attachments} />
          ) : (
            <TextCell text="" />
          ),
        size: 360,
      },
      {
        accessorKey: TEMPLATE_FUEL_CARD_PROVIDER_ID_KEY,
        header: (table) => (
          <ThSimpleSelect
            tableName={TEMPLATE_TABLE_KEY}
            id={TEMPLATE_FUEL_CARD_PROVIDER_ID_KEY}
            title={f({
              id: `table_list.columns.importTemplates.fuelCardProviderId`,
            })}
            // tooltip={f({ id: `table_list.columns.importTemplates.${PARKING_TAG_ACTIVE_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.fuelCardProvider ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: TEMPLATE_MIME_TYPE_GROUP_KEY,
        header: (table) => (
          <ThMultiSelect
            tableName={TEMPLATE_TABLE_KEY}
            id={TEMPLATE_MIME_TYPE_GROUP_KEY}
            title={f({ id: `table_list.columns.importTemplates.mimeTypeGroupId` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_TAG_CREATED_BY_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.mimeTypeGroup ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: TEMPLATE_ENCODING_KEY,
        header: (table) => (
          <ThSearch
            tableName={TEMPLATE_TABLE_KEY}
            id={TEMPLATE_ENCODING_KEY}
            title={f({ id: `table_list.columns.importTemplates.${TEMPLATE_ENCODING_KEY}` })}
            // tooltip={f({ id: `table_list.columns.importTemplates.${PARKING_TAG_NAME_KEY}.tooltip` })}
            isSortable={false}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.encoding ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
    ],
    [f]
  );

  const columnOrder = React.useMemo(() => {
    const order = columnsTable.map((item) => item.key);
    // add the detail and delete columns to the end
    // order.push(TAG_DETAIL_KEY, TAG_DELETED_AT_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc, column) => {
      // nastaví viditeľnosť na `true` alebo `false` na základe hodnoty `hidden` pre každý stĺpec
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
